import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Chip, Grid, IconButton, MenuItem, Paper, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityIcon from '@mui/icons-material/Visibility'

import { Context } from '../../../context/AuthContext'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import ExportMenu from '../../Shared/ExportMenu'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  cancelProductionOrder,
  getAllProductionOrders,
} from '../../../services/productionOrderServices'
import { resolveUsers } from '../../../services/directoryServices'
import { mensagemDeErro } from '../../../services/apiClient'
import {
  ORDER_STATUS_OPTIONS,
  getOrderStatus,
  isTerminalOrderStatus,
} from '../../../const/productionStatus'
import { formatDateTime, formatNumber } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'
import useUserNames from '../../../utils/useUserNames'

// A busca cobre nº da ordem, nº externo, produto, nome e código do cliente —
// é o mesmo OR que a API faz no parâmetro `search`.
const EMPTY_FILTERS = { search: '', status: '', dateFrom: '', dateTo: '' }

// Lotes de 200 (o teto da API) até somar o total, para o export levar TODAS as
// ordens do filtro, não só a página na tela.
const EXPORT_PAGE_SIZE = 200

const ProductionOrder = () => {
  const { subCompany } = useContext(Context)
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeCancelar = usePode(PERMISSOES.ORDEM_CANCELAR)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [orders, setOrders] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [applied, setApplied] = useState(EMPTY_FILTERS)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [orderToCancel, setOrderToCancel] = useState(null)

  const changeFilter = (field, value) => setFilters(current => ({ ...current, [field]: value }))

  // Texto entra por debounce (não bate na API a cada tecla); status e datas
  // aplicam na hora. Qualquer mudança de filtro volta para a primeira página.
  useDebounce(
    () => {
      setApplied(current => ({ ...current, search: filters.search }))
      setPage(0)
    },
    500,
    [filters.search]
  )

  useEffect(() => {
    setApplied(current => ({
      ...current,
      status: filters.status,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    }))
    setPage(0)
  }, [filters.status, filters.dateFrom, filters.dateTo])

  useEffect(() => {
    setPage(0)
  }, [subCompanyId])

  const fetchOrders = useCallback(async () => {
    if (!subCompanyId) {
      setOrders([])
      setTotal(0)
      return
    }
    setLoading(true)
    try {
      const response = await getAllProductionOrders({
        subCompanyId,
        status: applied.status,
        search: applied.search.trim(),
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
      setOrders(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      setOrders([])
      setTotal(0)
      notify(mensagemDeErro(error, 'Não foi possível carregar as ordens de produção.'), 'error')
    } finally {
      setLoading(false)
    }
  }, [subCompanyId, applied, page, rowsPerPage, notify])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // O contrato traz só o ID de quem criou; o nome vem do SSO, dono do cadastro.
  // Resolve apenas os IDs da página visível — um lote pequeno por vez.
  const responsibleIds = useMemo(
    () => orders.map(order => order.createdByUserId).filter(Boolean),
    [orders]
  )
  const { nomeDe } = useUserNames(responsibleIds)

  const handleCancel = async () => {
    setSaving(true)
    try {
      await cancelProductionOrder(orderToCancel.id)
      notify(
        'Ordem cancelada: o status foi alterado para Cancelado e o histórico foi mantido.',
        'success'
      )
      setOrderToCancel(null)
      fetchOrders()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível cancelar a ordem de produção.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  // Export: puxa TODAS as ordens do filtro atual, paginando a API em lotes, e
  // resolve o nome de todos os responsáveis de uma vez (a tabela só resolveu os
  // da página). Cai para o ID se o SSO não conhecer o usuário.
  const fetchAllForExport = useCallback(async () => {
    if (!subCompanyId) return []
    const todas = []
    let offset = 0
    for (;;) {
      const response = await getAllProductionOrders({
        subCompanyId,
        status: applied.status,
        search: applied.search.trim(),
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        limit: EXPORT_PAGE_SIZE,
        offset,
      })
      const lote = response.data.result || []
      todas.push(...lote)
      const totalDoFiltro = response.data.total || 0
      offset += EXPORT_PAGE_SIZE
      if (offset >= totalDoFiltro || lote.length === 0) break
    }

    const ids = [...new Set(todas.map(order => order.createdByUserId).filter(Boolean))]
    let nomePorId = new Map()
    if (ids.length > 0) {
      try {
        const usuarios = await resolveUsers(ids)
        nomePorId = new Map(
          usuarios.map(usuario => [String(usuario.id).trim().toUpperCase(), usuario.fullName])
        )
      } catch {
        // Sem o SSO, o export cai para o ID — melhor que travar a exportação.
      }
    }

    return todas.map(order => ({
      ...order,
      responsibleResolved: order.createdByUserId
        ? nomePorId.get(String(order.createdByUserId).trim().toUpperCase()) || order.createdByUserId
        : '—',
    }))
  }, [subCompanyId, applied])

  const columns = [
    {
      id: 'actions',
      label: 'Ações',
      width: 110,
      element: row => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Visualizar">
            <IconButton size="small" onClick={() => navigate(`/MovBreak/${row.id}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={podeCancelar ? 'Cancelar ordem' : 'Sem permissão para cancelar ordens'}
          >
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!podeCancelar || isTerminalOrderStatus(row.status)}
                onClick={() => setOrderToCancel(row)}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
    { id: 'numberBihands', label: 'Ordem Bihands', element: row => row.numberBihands || '—' },
    {
      id: 'numberOtherSystem',
      label: 'Ordem (outro sistema)',
      element: row => row.numberOtherSystem || '—',
    },
    { id: 'product', label: 'Produto', element: row => row.productName || '—' },
    {
      id: 'client',
      label: 'Cliente',
      element: row =>
        row.clientName ? [row.clientCode, row.clientName].filter(Boolean).join(' - ') : '—',
    },
    {
      id: 'quantity',
      label: 'Quantidade Total',
      align: 'right',
      element: row => formatNumber(row.quantityTotal),
    },
    {
      id: 'progress',
      label: 'Progresso',
      align: 'right',
      element: row => (row.progress ? `${Math.round(row.progress.percentComplete)}%` : '—'),
    },
    { id: 'responsible', label: 'Responsável', element: row => nomeDe(row.createdByUserId) },
    {
      id: 'status',
      label: 'Status',
      element: row => {
        const status = getOrderStatus(row.status)
        return <Chip size="small" label={row.statusLabel || status.label} color={status.color} />
      },
    },
    { id: 'created', label: 'Criado', element: row => formatDateTime(row.createdAt) },
  ]

  const exportColumns = [
    { header: 'Ordem Bihands', accessor: row => row.numberBihands || '' },
    { header: 'Ordem (outro sistema)', accessor: row => row.numberOtherSystem || '' },
    { header: 'Produto', accessor: row => row.productName || '' },
    {
      header: 'Cliente',
      accessor: row => (row.clientName ? [row.clientCode, row.clientName].filter(Boolean).join(' - ') : ''),
    },
    { header: 'Quantidade Total', accessor: row => formatNumber(row.quantityTotal) },
    {
      header: 'Progresso',
      accessor: row => (row.progress ? `${Math.round(row.progress.percentComplete)}%` : ''),
    },
    // `responsibleResolved` vem de fetchAllForExport (nome de todo o conjunto);
    // o fallback cobre um export direto da página, se houver.
    { header: 'Responsável', accessor: row => row.responsibleResolved || nomeDe(row.createdByUserId) },
    { header: 'Status', accessor: row => row.statusLabel || getOrderStatus(row.status).label },
    { header: 'Criado', accessor: row => formatDateTime(row.createdAt) },
  ]

  return (
    <Box>
      <PageHeader
        title="Ordens de Produção"
        subtitle="Acompanhe, crie e cancele as ordens de produção da filial."
        actions={
          <>
            <ExportMenu
              rows={orders}
              resolveRows={fetchAllForExport}
              columns={exportColumns}
              fileName="ordens-de-producao"
              title="Ordens de Produção"
              disabled={total === 0}
            />
            <PermissionComponent permissao={PERMISSOES.ORDEM_CRIAR}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/MovBreakCreate')}
              >
                Criar
              </Button>
            </PermissionComponent>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              size="small"
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar (nº ordem, produto ou cliente)"
              value={filters.search}
              onChange={event => changeFilter('search', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status}
              onChange={event => changeFilter('status', event.target.value)}
            >
              {ORDER_STATUS_OPTIONS.map(option => (
                <MenuItem key={String(option.value)} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom}
              onChange={event => changeFilter('dateFrom', event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo}
              onChange={event => changeFilter('dateTo', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button fullWidth color="inherit" onClick={() => setFilters(EMPTY_FILTERS)}>
              Limpar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={orders}
        loading={loading}
        emptyMessage={
          subCompanyId
            ? 'Nenhuma ordem de produção encontrada.'
            : 'Selecione uma filial para listar as ordens.'
        }
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowKey={row => row.id}
      />

      <ConfirmDialog
        open={Boolean(orderToCancel)}
        title="Cancelar esta ordem de produção?"
        description="A ordem passa para o status Cancelado e todo o histórico de quebras é mantido."
        confirmLabel="Cancelar ordem"
        cancelLabel="Voltar"
        loading={saving}
        onConfirm={handleCancel}
        onClose={() => setOrderToCancel(null)}
      />

      <LoadingBackdrop open={saving} message="Processando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProductionOrder
